import { faArrowRightLong } from '@fortawesome/pro-regular-svg-icons';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  backgroundColored,
  brand,
  fontDark,
  fontLight,
  fontMedium,
  secondary,
} from '@constants/colors';
import { comingDate } from '@constants/live-prep';
import { laptop, tablet } from '@constants/media-queries';
import { book } from '@constants/routes';
import { containerWidth } from '@constants/sizes';
import { space } from '@constants/spaces';
import Badge from '@elements/Badge';
import Button from '@elements/Button';
import List from '@elements/List';
import Text from '@elements/Text';
import Title from '@elements/Title';
import { Product } from '@graphql/generated/graphql';
import useMediaQuery from '@hooks/useMediaQuery';

import ProductFixedBar from './ProductFixedBar';
import ReviewsRating from './ReviewsRating';

const Component = styled.section`
  margin: 0 auto;
  padding: ${space * 4}px ${space * 4}px ${space * 5}px;

  max-width: ${containerWidth};

  ${tablet} {
    padding: ${space * 4}px ${space * 4}px ${space * 5}px;
  }

  ${laptop} {
    padding: ${space * 5}px ${space * 5}px ${space * 11}px;
  }
`;

const StyledCard = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: center;
  flex-direction: column;

  ${laptop} {
    justify-content: space-between;
    flex-direction: row;
  }
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Img = styled.div`
  position: relative;
  line-height: 0;
  margin-bottom: ${space * 2}px;
  img {
    border-radius: 12px;
  }

  ${laptop} {
    margin-bottom: ${space * 3}px;
  }
`;

const Content = styled.div`
  order: 3;
  & > a {
    display: inline-block;
  }

  ${laptop} {
    margin-left: ${space * 5}px;
    flex-basis: calc(100% - 518px - 40px); // img + margin
  }
`;

const StyledTitle = styled(Title)`
  font-size: 28px;
  line-height: 40px;
  font-weight: 800;

  ${laptop} {
    font-size: 40px;
    line-height: 48px;
  }
`;

const Body = styled.div`
  color: ${fontMedium};
  font-weight: 400;
  font-size: 16px;
  line-height: 32px;
  margin: ${space * 3}px 0;

  & a {
    color: ${brand};

    &:hover {
      color: ${fontLight};
    }
  }

  & strong {
    color: ${fontLight};
    font-weight: 500;
  }
`;

const Slider = styled.div<{ width: number }>`
  overflow-x: scroll;
  width: ${({ width }) => `${width}px`};
  margin-bottom: ${space * 3}px;
  order: 2;

  ${laptop} {
    margin-bottom: 0;
    order: 3;
  }
`;

const Photos = styled.div`
  display: flex;
`;

const Photo = styled.div<{ height: number }>`
  width: 40px;
  height: ${({ height }) => (height ? `${height}px` : '40px')};
  width: calc((100% - (8px * 4)) / 5);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border: 2px solid ${backgroundColored};
  border-radius: 12px;
  flex-shrink: 0;
  cursor: pointer;
  margin-right: ${space}px;
  transition: border-color 300ms ease;

  &:hover {
    border-color: ${brand};
  }
`;

const InfoMessage = styled(Text)`
  margin-top: ${space * 4}px;
  color: ${brand};
`;

const Price = styled.div`
  margin: ${space * 3}px 0;

  & > :first-child {
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    color: ${fontMedium};
  }

  & > :nth-child(2n) {
    font-weight: 600;
    font-size: 24px;
    line-height: 36px;
    color: ${fontDark};

    & > span {
      font-weight: 400;
    }
  }
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: ${space * 3}px;
  margin-bottom: ${space * 1}px;
`;

const StyledBadge = styled(Badge)`
  border-radius: 8px;
  background-color: ${secondary};
  font-weight: 600;
  color: white;
  font-size: 12px;
  padding: 0 ${space * 1}px;
`;

type BookDescriptionProps = {
  product?: Product;
};

const BookDescription = ({ product, ...rest }: BookDescriptionProps) => {
  const [image, setImage] = useState(0);
  const selectedImage = useRef() as React.MutableRefObject<HTMLDivElement>;
  const [imageHeight, setImageHeight] = useState(40);
  const { isTablet } = useMediaQuery();
  const [sliderWidth, setSliderWidth] = useState(518);

  useEffect(() => {
    const height = (selectedImage.current.offsetWidth - 4 * 8) / 5;
    setImageHeight(height);
    setSliderWidth(selectedImage.current.offsetWidth);
  }, [selectedImage, isTablet]);

  if (!product) return null;

  const usps = [
    "Weinig werk voor jou: deelnemers vullen hun eigen pagina's",
    'Nodig uit via Whatsapp, email, etc',
    'Pas vormgeving geheel naar eigen wens aan',
  ];

  const productPics = (
    <Slider width={sliderWidth}>
      <Photos>
        {product?.images?.map((img, i) => (
          <Photo
            key={i}
            style={{ backgroundImage: `url(${img})` }}
            onClick={() => setImage(i)}
            height={imageHeight}
          />
        ))}
      </Photos>
    </Slider>
  );

  return (
    <Component {...rest}>
      <StyledCard>
        <Container>
          <Img ref={selectedImage}>
            {product.image && (
              <Image
                src={product.images?.[image] || product.image}
                alt={product.title || 'Boek afbeelding'}
                width={518}
                height={518}
                unoptimized={true}
                priority
              />
            )}
          </Img>
          {!isTablet && productPics}
        </Container>
        {isTablet && productPics}
        <Content>
          <Info>
            <ReviewsRating />
            <StyledBadge>Maak samen een fotoboek!</StyledBadge>
          </Info>
          <StyledTitle size="lg" className="mb-16">
            {product.introTitle}
          </StyledTitle>
          <List items={usps} />
          <Price>
            <p>Vanaf</p>
            <p>
              <span>&euro;</span> 27,50
            </p>
          </Price>
          {product.hideCTA ? (
            <InfoMessage size="nm">
              Vanaf {comingDate()} beschikbaar!
            </InfoMessage>
          ) : (
            <Link href={book(product.bookID, '')}>
              <a>
                <Button size="big" iconRight={faArrowRightLong}>
                  Begin nu
                </Button>
              </a>
            </Link>
          )}
          <ProductFixedBar bookID={product.bookID} />
          <Body
            dangerouslySetInnerHTML={{ __html: product.description || '' }}
          />
        </Content>
      </StyledCard>
    </Component>
  );
};

export default BookDescription;
